import type { TeamMember } from "../../../../shared/data/api/teamApi";

/**
 * Rules for the Escalations & access panel.
 *
 * THE ESCALATION TABLE EXISTS NOW, BUT IT IS DISPLAY-ONLY. The onboarding
 * wizard collects an ordered list of escalation contacts — "first in the list
 * is notified first" — and master's go-live push writes it into the tenant's
 * `escalation_contacts` (wholesale refresh per run; master is the only
 * writer). The panel shows that list, read-only, at the top.
 *
 * What has NOT changed: `EmailNotificationService` still does not read it.
 * Recipients are chosen by ROLE, in SQL — no ordering, no per-person
 * preference. Wiring the mailer to the pushed order is the recorded
 * follow-up, so the digest report below stays the truth about who actually
 * gets emailed. (`email_notifications` remains an outbox, not configuration,
 * and vendors' `escalation_contact` column still means something else
 * entirely.)
 */

/**
 * Roles that receive the team digests.
 *
 * Mirrors `getAccountantDigestEligibleUsers` and `getWorkSummaryEligibleUsers`
 * exactly, including their tolerance for two misspellings that evidently
 * reached production data at some point. Lowercased and trimmed the same way
 * the SQL does.
 */
const TEAM_DIGEST_ROLES = [
  "admin",
  "accountant",
  "cfo",
  "senior accountant",
  "senior_accountant",
  "senrio accountant",
  "senrio_accountant",
];

/** Roles that receive the client digests, per `getClientViewUsers`. */
const CLIENT_DIGEST_ROLES = ["client_view", "client view"];

const normaliseRole = (member: TeamMember): string =>
  (member.role ?? "").trim().toLowerCase();

/**
 * The mailer also requires a non-empty email and an active account, so a
 * deactivated person silently stops being notified. Matching that here keeps
 * the list truthful rather than aspirational.
 */
const isMailable = (member: TeamMember): boolean =>
  member.isActive && member.email.trim() !== "";

export const receivesTeamDigest = (member: TeamMember): boolean =>
  isMailable(member) && TEAM_DIGEST_ROLES.includes(normaliseRole(member));

export const receivesClientDigest = (member: TeamMember): boolean =>
  isMailable(member) && CLIENT_DIGEST_ROLES.includes(normaliseRole(member));

/** Client logins, active or not — the second half of the panel. */
export const clientLogins = (members: TeamMember[]): TeamMember[] =>
  members.filter((m) => CLIENT_DIGEST_ROLES.includes(normaliseRole(m)));

/**
 * Someone whose role matches nothing the mailer looks for.
 *
 * Worth surfacing because it is silent: a person with a custom role created
 * under /admin is simply never emailed, and nothing anywhere says so.
 */
export const receivesNothing = (member: TeamMember): boolean =>
  member.isActive &&
  !receivesTeamDigest(member) &&
  !receivesClientDigest(member);

/** "12 May 2026", or null when they have never signed in. */
export const formatLastSeen = (value: string | null): string | null => {
  if (value === null) return null;
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};
